/*
 * Поле ввода в стиле ОТВЕТО. Порт otveto/apps/web/src/components/common/ui/Input.pcss.
 *
 * Отличие от MUI по умолчанию: лейбл всегда снаружи и сверху, без shrink-анимации,
 * fieldset у OutlinedInput убран в теме, а рамку рисует .field.
 */

.wrapper {
	display: flex;
	flex-direction: column;
	gap: 8px;
	width: 100%;
	/* Без min-width поле не сжимается внутри flex- и grid-контейнеров: собственная
	   ширина input'а (особенно type=time с иконкой) перебивает ширину колонки */
	min-width: 0;
}

.label {
	color: var(--input-title);
	font-size: 16px;
	font-weight: 500;
	line-height: 150%;
}

.labelDisabled {
	color: var(--input-title-disabled);
}

/* Пометка рядом с названием поля — например «авто» у подтянутого из ЕГРЮЛ. */
.labelAdornment {
	margin-left: 8px;
	vertical-align: middle;
}

.required {
	color: var(--text-error);
	margin-left: 2px;
}

.field {
	background: var(--input-bg);
	border: 1px solid var(--input-border);
	box-sizing: border-box;
	position: relative;
	overflow: hidden;
	transition:
		border-color 0.2s ease-in-out,
		background-color 0.2s ease-in-out;
	padding: 0;
}

.field:hover,
.focused {
	border-color: var(--input-border-hover);
}

.error {
	border-color: var(--input-border-error) !important;
}

.disabled {
	border-color: var(--border-main-quarteary);
	background: var(--bg-dark-quartiary);
}

.small {
	border-radius: var(--Border-radius-S);
	min-height: 44px;
}

.medium {
	border-radius: var(--Border-radius-M);
	min-height: 56px;
}

.input {
	color: var(--input-text);
	font-size: 16px;
	font-weight: 400;
	line-height: 150%;
	letter-spacing: 0.16px;
}

/*
 * Геометрия — составным селектором, а не тем же одним классом выше.
 *
 * Своему `InputBase-input` MUI пишет `padding: 4px 0 5px`, `height: 1.4375em`
 * и `box-sizing: content-box` одиночным классом emotion, то есть весом 0-1-0 —
 * ровно как у `.input`. При равном весе выигрывает вставленный позже, а <style>
 * emotion встаёт в head после CSS-модулей: побеждал MUI, и в кабинете поле
 * оставалось совсем без боковых отступов — текст упирался в рамку, — а сам
 * <input> был 23px внутри 56px рамки, так что клик мимо этой полоски поле
 * не фокусировал.
 *
 * Видно это было только в кабинете и только у средних полей. На экранах входа
 * те же значения возвращает своим правилом AuthWrapper.module.css, а у мелких
 * `.small .input` ниже и так весит 0-2-0 — средним просто не досталось веса,
 * с которым мелкие живут с самого начала.
 *
 * Подняты только эти три свойства. Цвет и шрифт остаются весом 0-1-0 намеренно:
 * страницы перебивают их правилами вида `.codeInput input` (вес 0-1-1,
 * WarehousePrintersPage.module.css), и подняв вес здесь, мы бы молча отменили их —
 * моноширинный код подключения принтера снова стал бы обычным текстом.
 */
.field .input {
	padding: 0 16px;
	height: 54px;
	box-sizing: border-box;
}

/*
 * Значение выпадающего списка — это <div>, а не <input>. Строку внутри <input>
 * браузер центрирует по высоте сам, а в блоке она прижимается к верху, и с
 * высотой поля из правила выше выбранный вариант встал бы под самой рамкой.
 *
 * Отдельным классом, а не `:global(.MuiSelect-select)` в этом же правиле: тот же
 * `.input` MUI получает и как настоящий <input>, и как значение списка, а класс
 * читается на месте вызова. PhoneCountrySelect центрирует себя своим правилом —
 * ему флекс нужен ещё и под флаг рядом с кодом.
 */
.field .selectValue {
	display: flex;
	align-items: center;
}

.small .input {
	height: 42px;
	padding: 0 12px;
	font-size: 14px;
	letter-spacing: 0.14px;
}

/*
 * Высота значения списка — с удвоенным классом. MUI пишет ему `height: auto` правилом
 * `.css-….MuiSelect-select` (вес 0-2-0), и оно подключается позже модуля: при равном
 * весе побеждало оно, блок значения сжимался до строки текста, и клик по верхней
 * и нижней полосе поля список не открывал — они принадлежали обёртке, а не значению.
 */
.medium .selectValue.selectValue {
	height: 54px;
}

.small .selectValue.selectValue {
	height: 42px;
}

.input::placeholder {
	color: var(--input-placeholder);
	opacity: 1;
}

.input:disabled {
	color: var(--input-text-disabled);
	-webkit-text-fill-color: var(--input-text-disabled);
}

/* Убираем стрелки у number: они ломают выравнивание и всё равно не используются */
.input[type='number']::-webkit-inner-spin-button,
.input[type='number']::-webkit-outer-spin-button {
	-webkit-appearance: none;
	margin: 0;
}

/*
 * Отступы многострочного поля — через три класса, а не через один.
 *
 * MUI задаёт своему `inputMultiline` нулевой padding составным селектором
 * (класс emotion + `.MuiInputBase-inputMultiline`), то есть специфичностью 0-2-0.
 * Одиночный `.multiline` её не перебивал, и textarea оставалась совсем без полей:
 * текст упирался в рамку слева и сверху. Проверено в браузере — у всех
 * многострочных полей приложения `padding: 0px`.
 *
 * Ровный `padding`, а не только вертикальный: горизонтальный у `.input` тоже
 * съедался тем же правилом, и писать его отдельно значило бы чинить половину.
 *
 * По вертикали вдвое меньше, чем по горизонтали, и меньше, чем у однострочного
 * поля. У однострочного отступ задан фиксированной высотой в 54px и держит поле
 * на одной линейке с кнопками; многострочное растёт само, и те же 16px сверху
 * и снизу дают воздух, которого текст не просил.
 */
.field .input.multiline {
	height: auto;
	padding: 8px 16px;
}

.field.small .input.multiline {
	padding: 8px 12px;
}

.helper {
	color: var(--input-caption);
	font-size: 14px;
	font-weight: 400;
	line-height: 170%;
	letter-spacing: 0.14px;
	margin: 0;
}

.helperError {
	color: var(--text-error);
}

.helperRow {
	display: flex;
	justify-content: space-between;
	gap: 12px;
}

.counter {
	color: var(--text-tertiary);
	font-size: 14px;
	white-space: nowrap;
}

/*
 * Флексом, а не просто отступом: внутри span содержимое выстраивалось по базовой
 * линии текста, и кнопка-крестик вставала ниже середины поля. Иконки этого не
 * показывали — у SvgIcon своя выключка, — поэтому и было незаметно.
 */
.endAdornment {
	display: inline-flex;
	align-items: center;
	margin-right: 8px;
}
